<template>
	<section class="store-content store-content--get-started">
		<div class="row">
			<div class="col-xxs-12 col-sm-4">
				<div class="start-block__topper">
					<h2 class="start-block__headline">
						<i class="icon icon-red icon-get-started icon-user soft-half-right"></i>
						<span class="start-block__headline__text">{{ userGreeting }}</span>
					</h2>
				</div>

				<div class="start-block__content push-bottom">
					<p v-if="user.isGuest">You're currently browsing as a guest. To track past orders or for a quicker checkout, you can create an account by
						<button
							@click="$emit( 'guest_wants_to_login' )"
							class="link link--inline text--red"
						>clicking here!</button>
					</p>

					<p v-else>To view past orders or change your account information,
						<a
							:href="my_account_url"
							class="link link--inline text--red"
						>click here.</a></p>
				</div>
			</div>

			<div class="col-xxs-12 col-sm-8">
				<div class="row">
					<div
						:class="orderTypeBlockClasses"
						class="col-xxs-12"
					>
						<div class="start-block__topper">
							<h2 class="start-block__headline">
								<i class="icon icon-red icon-get-started icon-shopping-bag soft-half-right"></i>
								<span class="start-block__headline__text">Order type</span>
							</h2>
						</div>
						<fieldset
							v-if="deliveryIsEnabled || pickupIsEnabled"
							:class="{ 'is-loading': is_fetching_ordertypes }"
							class="ordertypes push-bottom"
						>

							<div
								v-if="orderTypeError.length"
								class="ordertype-error"
							>{{ orderTypeError }}</div>

							<label
								v-show="deliveryIsEnabled"
								for="ordertype_delivery" class="ordertype-field">
								<input
									@input="handleSelectOrderType( 'delivery' )"
									:checked="'delivery' === activeOrderType"
									id="ordertype_delivery"
									class="ordertype-field__value input-choice input-choice--radio"
									name="user_ordertype"
									value="delivery"
									type="radio"
								/>
								<span 
									v-html="deliveryOptionLabel"
									class="ordertype-field__label input-choice-value input-choice-value--radio"
								></span>
							</label>

							<label
								v-show="pickupIsEnabled && pickupIsOpen"
								for="ordertype_pickup" class="ordertype-field"
							>
								<input
									@input="handleSelectOrderType( 'pickup' )"
									:checked="'pickup' === activeOrderType"
									id="ordertype_pickup"
									class="ordertype-field__value input-choice input-choice--radio"
									name="user_ordertype"
									value="pickup"
									type="radio"
								/>
								<span 
									v-html="messages.label_option_pickup"
									class="ordertype-field__label input-choice-value input-choice-value--radio"
								></span>
							</label>
						</fieldset>

						<div
							v-if="this.deliveryIsEnabled && !this.deliveryIsOpen"
							v-html="messages.delivery_inactive"
							class="ordertype-msg-closed ordertype-msg-closed--delivery"
						></div>

						<div
							v-if="this.pickupIsEnabled && !this.pickupIsOpen"
							v-html="messages.pickup_closed"
							class="ordertype-msg-closed ordertype-msg-closed--pickup"
						></div>
					</div>

					<div
						v-show="'pickup' === activeOrderType"
						class="col-xxs-12 col-sm-6"
					>
						<div class="start-block__topper">
							<h2 class="start-block__headline">
								<i class="icon icon-red icon-get-started icon-maps-and-flags soft-half-right"></i>
								<span class="start-block__headline__text">Location</span>
							</h2>
						</div>

						<Map
							@user_selected_location="handleSelectLocation"
							:google_api_key="google_api_key"
							:locations="locations"
						/>

					</div>
				</div>
			</div>
		</div>

		<div class="row push-top soft-top">
			<div class="col-xxs-12 text-right">
				<button
					v-if="readyToAddItems"
					@click="$emit( 'go_to_select_items', 'Select Items' )"
					class="btn btn--red text--uppercase"
				>
					Proceed to Select Items
				</button>
			</div>
		</div>

	</section>
</template>


<script>
	import Map from '../components/Map.vue'

	export default {
		name: 'GetStarted',
		components: {
			Map,
		},

		props: {
			user: {
				type: Object,
				required: true,
			},
			locations: {
				type: Array,
				default() {
					return []
				},
			},
			google_api_key: {
				type: String,
				default: '',
			},
			messages: {
				type: Object,
				required: true,
			},
			order_types: {
				type: Object,
				required: true,
			},
			is_fetching_ordertypes: {
				type: Boolean,
				default: false,
			},
			my_account_url: {
				type: String,
				required: '',
			},
		},



		data() {
			return {
				orderTypeError: '',
				userHasInteracted: false,
			}
		},


		computed: {

			/**
			 * Shortcut to get user-selected order type
			 * 
			 * @return	{string}	Order type
			 */
			activeOrderType() {
				for( const [ orderTypeName, details ] of Object.entries( this.order_types ) ) {
					if( details.isEnabled && details.isSelected ) {
						return orderTypeName
					}
				}

				return ''
			},


			/**
			 * Quick check to see if the delivery order type is enabled and available to select
			 * 
			 * @return	{bool}	True, if enabled; otherwise, false
			 */
			deliveryIsEnabled() {
				if( this.user.isPennMed ) {
					return this.order_types.delivery.isEnabled
				}

				return false
			},


			/**
			 * Quick check to see if SCW has already delivered to Penn Med today
			 * 
			 * @return	{bool}	True, if delivered; otherwise, false (or if user is not Penn Med)
			 */
			deliveryHasClosedForToday() {
				if( this.user.isPennMed ) {
					return this.order_types.delivery.hasDelivered
				}

				return false
			},


			/**
			 * Get delivery messaging based on whether delivery has already happened for today
			 * 
			 * @return	{string} 	Delivery messaging
			 */
			deliveryOptionLabel() {
				if( this.order_types.delivery.hasDelivered ) {
					return this.messages.label_option_delivery_closed
				} else {
					return this.messages.label_option_delivery
				}
			},


			/**
			 * Quick check to see if the pickup order type is enabled and available to select
			 * 
			 * @return	{bool}	True, if enabled; otherwise, false
			 */
			pickupIsEnabled() {
				return this.order_types.pickup.isEnabled
			},


			/**
			 * Quick check to see if SCW is still accepting pickup orders for today
			 * 
			 * @return	{bool}	True, if still accepting; otherwise, false
			 */
			pickupIsOpen() {
				return this.order_types.pickup.isOpen
			},


			/**
			 * Customize welcome message depending on whether user has logged in or not
			 * 
			 * @return	{string} 	Messaging
			 */
			userGreeting() {
				let username = ''

				if( this.user.displayName.length ) {
					username = this.user.displayName
				} else if( this.user.isGuest ) {
					username = 'Guest'
				}

				if( username.length ) {
					return `Hi ${username}!`
				} else {
					return 'Hi there!'
				}
			},


			/**
			 * Bootstrap classes for order type column
			 * 
			 * @return	{string} 	Classes
			 */
			orderTypeBlockClasses() {
				const classes = []

				if( 'pickup' === this.activeOrderType ) {
					classes.push( 'col-sm-6' )
				}

				return classes.join( ' ' )
			},


			/**
			 * Get user-selected location
			 * 
			 * @return	{mixed} 	Location obj is location has been selected; otherwise, undefined
			 */
			getSelectedLocation() {
				return this.locations.find( location => location.isSelected )
			},


			/**
			 * If user has selected location and order type, then show link to prompt user to add items
			 * 
			 * @return	{bool}	True, if good to show link; otherwise, false
			 */
			readyToAddItems() {
				const hasOrderType 	= ( '' !== this.activeOrderType ),
					hasLocation 	= !!this.getSelectedLocation

				if( this.user.isPennMed ) {
					return hasOrderType
				} else if( this.user.isGuest || this.user.IsLoggedIn ) {
					return ( hasOrderType && hasLocation )
				}

				return false
			},
		},



		methods: {

			/**
			 * Validates user-selected order type
			 * 
			 * @param	{string} 	orderType 	User-selected order type
			 */
			handleSelectOrderType( orderType ) {
				this.userHasInteracted = true

				// reset error
				this.orderTypeError = ''

				// double-check that ordertype is valid (enabled, is open, appropriate for current user)
				switch( orderType ) {
					case 'delivery':
						if( !this.user.isPennMed ) {
							this.orderTypeError = 'Delivery is only available for Penn Med users'

						} else if( !this.isEnabled ) {
							this.orderTypeError = 'Delivery is not available'

						} else {
							this.setOrderType( 'delivery' )
							return
						}

						break

					case 'pickup':
						if( !this.isEnabled ) {
							this.orderTypeError = 'Pickup is not available'

						} else if( !this.pickupIsOpen ) {
							this.orderTypeError = 'Pickup is currently closed'

						} else {
							this.setOrderType( 'pickup' )
							return
						}

						break
				}

				// if invalid, clear out order type
				this.setOrderType( '' )
			},


			/**
			 * Sets order type by propagating user-selected order type to parent component
			 * 
			 * @param	{string} 	orderType 	User-selected order type
			 */
			setOrderType( orderType ) {
				this.userHasInteracted = true

				console.log( orderType )

				this.$emit( 'user_selected_order_type', orderType )
			},


			/**
			 * Sets location by propagating user-selected location to parent component
			 * 
			 * @param	{string} 	locationId 	Post ID for user-selected location
			 */
			handleSelectLocation( locationId ) {
				this.userHasInteracted = true
				
				this.$emit( 'user_selected_location', locationId )
			},
		},
	}
	
</script>
